import { CommonCaseTypeResponse } from '@/api/services/common/case-type/common-case-type.dto'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import { CommonPortionTypeResponse } from '@/api/services/common/portion-type/common-portion-type.dto'
import { CommonSubportionTypeResponse } from '@/api/services/common/subportion-type/common-subportion-type.dto'
import { CommonAssayResponse } from '@/api/services/common/assay/common-assay.dto'
import {
    CommonLaboratoryResponse,
    CommonPetitionerResponse,
    CommonUserappResponse,
    EnumResponse
} from '@/api/services/common.dto'
import { BillableConceptResponse } from '@/api/services/billing/common-billable-concept/billing-common-billable-concept.dto'
import { BillableRateResponse } from '@/api/services/billing/common-billable-rate/billing-common-billable-rate.dto'
import { CommonPersonnelResponse } from '@/api/services/common/personnel/common-personnel.dto'
import { CommonResponsibilityResponse } from '@/api/services/common/responsibility/common-responsibility.dto'
import { PredefinedCustomNotificationResponse } from '@/modules/configurationModule/detail/notifications/services/predefined-custom-notification/predefined-custom-notification.dto'
import { CommonDepartmentResponse } from '@/api/services/common/department/common-department.dto'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { CustomerResponse } from '@/api/services/billing/common-customer/billing-common-customer.dto'
import { BillableDeliveryNoteResponse } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { CommonInsurerResponse } from '@/api/services/common/insurer/common-insurer.dto'
import { CommonPatientResponse } from '@/api/services/common/patient/common-patient.dto'
import { CommonRegimeResponse } from '@/api/services/common/case/common-case.dto'
import { AccountingUnitResponse } from '@/api/services/billing/common-accounting-unit/billing-common-accounting-unit.dto'

export interface BillingCaseResponse {
    id: number
    caseNumber: string
    caseType: CommonCaseTypeResponse
    pathologist: StrictNullable<CommonPersonnelResponse>
    physicianDepartment: StrictNullable<CommonDepartmentResponse>
    physicianPetitioner: StrictNullable<CommonPetitionerResponse>
    insurer: CommonInsurerResponse
    referralNumber: StrictNullable<string>
    insuranceNumber: StrictNullable<string>
    receptionDate: string
    validationDate: StrictNullable<string>
    errorAutomaticAssignment: boolean
    patient: CommonPatientResponse
    regime: CommonRegimeResponse
}
export interface BillingSampleResponse {
    id: number
    sampleLabel: string
    sampleType: CommonSampleTypeResponse
    topographicCode: CommonTopographicCodeResponse
}
export interface BillingPortionResponse {
    id: number
    portionLabel: string
    portionType: CommonPortionTypeResponse
}
export interface BillingSubportionResponse {
    id: number
    subportionLabel: string
    subportionType: CommonSubportionTypeResponse
    assay: CommonAssayResponse
}

export interface AppliedBillableConceptEditionDisabledProperties {
    customer: boolean
    rate: boolean
    addToInvoice: boolean
    isForAutoinvoice: boolean
    value: boolean
    unit: boolean
    forAutoinvoice: boolean
}

export interface AppliedBillableConceptOperationGuards {
    canBeAddedToDeliveryNote: boolean
    canBeDeleted: boolean
}

export interface AppliedBillableConceptMetadataResponse {
    editionDisabledFields: AppliedBillableConceptEditionDisabledProperties
    operationGuards: AppliedBillableConceptOperationGuards
}

export enum AppliedBillableConceptEntityType {
    CASE = 'CASE',
    SAMPLE = 'SAMPLE',
    PORTION = 'PORTION',
    SUBPORTION = 'SUBPORTION'
}
export interface AppliedBillableConceptEntityTypeEnumResponse extends EnumResponse {
    code: AppliedBillableConceptEntityType
}

export enum BillingCopaymentType {
    PATIENT = 'PATIENT',
    CUSTOMER = 'CUSTOMER'
}
export interface BillingCopaymentTypeEnumResponse extends EnumResponse {
    code: BillingCopaymentType
}

export interface AppliedBillableConceptResponse {
    id: number
    entityType: AppliedBillableConceptEntityTypeEnumResponse
    entityId: number
    entityLabel: string
    laboratory: CommonLaboratoryResponse
    concept: BillableConceptResponse
    referenceCode: StrictNullable<string>
    alias: StrictNullable<string>
    caseStudy: BillingCaseResponse
    sample: StrictNullable<BillingSampleResponse>
    portion: StrictNullable<BillingPortionResponse>
    subportion: StrictNullable<BillingSubportionResponse>
    value: StrictNullable<number>
    unit: StrictNullable<AccountingUnitResponse>
    customer: StrictNullable<CustomerResponse>
    rate: StrictNullable<BillableRateResponse>
    creationDate: string
    creationUser: CommonUserappResponse
    lastModificationDate: string
    lastModificationUser: CommonUserappResponse
    automatic: boolean
    requiresAuthorization: boolean
    authorizationCode: StrictNullable<string>
    authorizationRequested: boolean
    addToInvoice: boolean
    reasonIfNotToAddToInvoice: StrictNullable<string>
    isForAutoInvoice: boolean
    deliveryNote: StrictNullable<BillableDeliveryNoteResponse>
    autoInvoiceSystemOperationCode: StrictNullable<string>
    autoInvoiceSystemInError: StrictNullable<boolean>
    autoInvoiceSystemErrorDescription: StrictNullable<string>
    hasCopayments: boolean
    isCopayment: boolean
    copaymentType: StrictNullable<BillingCopaymentTypeEnumResponse>
    copaymentParent: StrictNullable<AppliedBillableConceptResponse>
    metadata: AppliedBillableConceptMetadataResponse
}

export interface AppliedBillableConceptPatchInBatchRequest {
    id: number
    conceptId?: Nullable<number>
    customerId?: Nullable<number>
    rateId?: Nullable<number>
    addToInvoice?: Nullable<boolean>
    reasonIfNotToAddToInvoice?: Nullable<string>
    authorizationCode?: Nullable<string>
}
export interface AppliedBillableConceptExtraFieldsPatchInBatchRequest extends AppliedBillableConceptPatchInBatchRequest {
    value?: Nullable<number>
    unitId?: Nullable<number>
    isForAutoInvoice?: Nullable<boolean>
    autoInvoiceSystemOperationCode?: StrictNullable<string>
    autoInvoiceSystemInError?: StrictNullable<boolean>
    autoInvoiceSystemErrorDescription?: StrictNullable<string>
}

export interface AppliedBillableConceptCreateRequest {
    entityType: AppliedBillableConceptEntityType
    conceptId: number
    customerId?: Nullable<number>
    rateId?: Nullable<number>
    caseId: number
    sampleId?: Nullable<number>
    portionId?: Nullable<number>
    subportionId?: Nullable<number>
    addToInvoice?: Nullable<boolean>
    reasonIfNotToAddToInvoice?: Nullable<string>
    authorizationCode?: Nullable<string>
}
export interface AppliedBillableConceptExtraFieldsCreateRequest extends AppliedBillableConceptCreateRequest{
    value?: Nullable<number>
    unitId?: Nullable<number>
    isForAutoInvoice?: Nullable<boolean>
}

export interface SentCustomNotificationResponse {
    id: number
    subject: string
    body: string
    notifiedPetitioner: CommonPetitionerResponse
    notifiedDepartment: CommonDepartmentResponse
    laboratory: CommonLaboratoryResponse
    type: string
    relatedEntityId: number
    timestamp: string
    createdBy: CommonUserappResponse
    notifiedPersonnelList: CommonPersonnelResponse[]
    notifiedResponsibilities: CommonResponsibilityResponse[]
    predefinedCustomNotification: PredefinedCustomNotificationResponse
}

export interface SumPerAccountingUnitResponse {
    accountingUnit: AccountingUnitResponse
    sumValue: number
}

export interface HierarchicalBillingSubportionTreeResponse {
    subportionId: number
    subportionNumber: string
    subportionLabel: string
    subportionType: CommonSubportionTypeResponse
    billableValue: number
}
export interface HierarchicalBillingPortionTreeResponse {
    portionId: number
    portionNumber: string
    portionLabel: string
    portionType: CommonPortionTypeResponse
    subportions: HierarchicalBillingSubportionTreeResponse[]
    billableValue: number
}
export interface HierarchicalBillingSampleTreeResponse {
    sampleId: number
    sampleNumber: string
    sampleLabel: string
    sampleType: CommonSampleTypeResponse
    portions: HierarchicalBillingPortionTreeResponse[]
    billableValue: number
}
export interface HierarchicalBillingCaseTreeResponse {
    caseId: number
    caseNumber: string
    caseType: CommonCaseTypeResponse
    samples: HierarchicalBillingSampleTreeResponse[]
    billableValue: number
}
